import { EyeOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { Button, Card, Col, Flex, Input, Row, Select, Table } from 'antd'
import type { ColumnsType, SorterResult, TablePaginationConfig } from 'antd/es/table/interface'
import { FilterValue } from 'antd/es/table/interface'
import { PagingParams } from 'Modules'
import { useState } from 'react'

import { AppAdmin } from 'api/app-admin'
import LinkAddress from 'component/link/link-address'
import {
  USER_MEY_REAL_STATUS_OPTIONS,
  UserMeyRealKycStatusEnum,
  UserMeyRealStatusEnum,
} from 'constants/user-mey-real'
import useSearchParams from 'hooks/use-search-params'
import { ModalReferralPerformance } from 'modules/user-mey-real/model-referral-performance'

interface ReferralUserRow {
  _id: string
  walletAddress: string
  email?: string
  refCode?: string
  kycStatus?: UserMeyRealKycStatusEnum
  status?: UserMeyRealStatusEnum
  totalReferrer?: number
  totalEarned?: number
  ptoPurchased?: number
}

interface ReferralUsersParams extends PagingParams {
  search?: string
  status?: UserMeyRealStatusEnum | null
}

const SORT_FIELD_MAP: Record<string, string> = {
  totalReferrer: 'totalReferrer',
  totalEarned: 'totalEarned',
}

export const PtoReferralUsersTable = () => {
  const { searchParams, setSearchParams, initPagination, onSearch } =
    useSearchParams<ReferralUsersParams>({})
  const [referralPerformanceAddress, setReferralPerformanceAddress] = useState<string | undefined>()

  const { data, isLoading } = useQuery({
    queryKey: ['referralClaimHistory', searchParams],
    queryFn: async () => {
      const res = await AppAdmin.getReferralClaimHistory(searchParams)
      return {
        results: (res.data?.data?.results || []) as ReferralUserRow[],
        total: res.data?.data?.totalResults ?? 0,
      }
    },
    gcTime: 30000,
  })

  const users = data?.results ?? []
  const total = data?.total ?? 0

  const handleTableChange = (
    pagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter: SorterResult<ReferralUserRow> | SorterResult<ReferralUserRow>[]
  ) => {
    const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter
    const sortField = singleSorter?.columnKey
      ? SORT_FIELD_MAP[singleSorter.columnKey as string]
      : undefined
    const sortOrder = singleSorter?.order
      ? singleSorter.order === 'ascend'
        ? 'asc'
        : 'desc'
      : undefined
    const sort = sortField && sortOrder ? `${sortField}:${sortOrder}` : undefined

    setSearchParams((pre) => ({
      ...pre,
      page: pagination.current,
      limit: pagination.pageSize,
      sort,
    }))
  }

  const columns: ColumnsType<ReferralUserRow> = [
    {
      title: 'Wallet Address',
      dataIndex: 'walletAddress',
      key: 'walletAddress',
      width: 200,
      render: (text: string) => <LinkAddress address={text} type="address" />,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 220,
    },
    {
      title: 'Referral Code',
      dataIndex: 'refCode',
      key: 'refCode',
      width: 180,
      render(value: string | undefined, row) {
        if (!value) return null

        return (
          <Row gutter={8} align="middle">
            <Col>{value}</Col>
            <Col>
              <Button
                type="text"
                icon={<EyeOutlined />}
                onClick={() => setReferralPerformanceAddress(row.walletAddress)}
                size="small"
              />
            </Col>
          </Row>
        )
      },
    },
    {
      title: 'Total Referrals',
      dataIndex: 'totalReferrer',
      key: 'totalReferrer',
      width: 150,
      align: 'center',
      sorter: true,
      render: (value?: number) => value ?? 0,
    },
    {
      title: 'Total Earned',
      dataIndex: 'totalEarned',
      key: 'totalEarned',
      width: 160,
      align: 'center',
      sorter: true,
      render: (value?: number) => `${(value ?? 0).toLocaleString()} MEY`,
    },
  ]

  return (
    <>
      <Card style={{ marginBottom: 16 }}>
        <Flex gap={12} wrap="wrap">
          <Input.Search
            placeholder="Search by email or referral code..."
            value={searchParams.search || ''}
            onChange={(e) => onSearch({ search: e.target.value } as ReferralUsersParams)}
            allowClear
            style={{ flex: 1, minWidth: 240 }}
          />
          <Select
            placeholder="All Status"
            options={USER_MEY_REAL_STATUS_OPTIONS}
            value={searchParams.status ?? null}
            onChange={(value) => onSearch({ status: value } as ReferralUsersParams)}
            style={{ width: 180 }}
          />
        </Flex>
      </Card>

      <Table<ReferralUserRow>
        columns={columns}
        dataSource={users}
        rowKey={(record) => record._id || record.walletAddress}
        loading={isLoading}
        pagination={initPagination(total)}
        onChange={handleTableChange}
        scroll={{ x: 1000 }}
        bordered
      />

      {!!referralPerformanceAddress && (
        <ModalReferralPerformance
          walletAddress={referralPerformanceAddress}
          onClose={() => setReferralPerformanceAddress(undefined)}
        />
      )}
    </>
  )
}
